import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, InputNumber, Select, Tag, Card, Row, Col, Statistic, message, Space, Alert, Checkbox, Popconfirm, Grid, DatePicker } from 'antd';
import { CheckCircleOutlined, CheckOutlined, ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import { musterilerAPI, gununMenuleriAPI, satislarAPI, gunUretimPlaniAPI, urunlerAPI, listeFiyatlariAPI } from '../services/api';
import { useLookups } from '../hooks/useLookups';
import { formatPara } from '../utils/format';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';

dayjs.locale('tr');

const PAKETLEME_RENK = { 1: 'blue', 2: 'orange' };

const SABIT_GELIR_TIPI_ID = 2;

const gunKeyMap = {
  1: 'pazartesi_kisi',
  2: 'sali_kisi',
  3: 'carsamba_kisi',
  4: 'persembe_kisi',
  5: 'cuma_kisi',
  6: 'cumartesi_kisi',
  0: 'pazar_kisi',
};

const gunAdlari = {
  1: 'Pazartesi',
  2: 'Salı',
  3: 'Çarşamba',
  4: 'Perşembe',
  5: 'Cuma',
  6: 'Cumartesi',
  0: 'Pazar',
};

function Paketleme() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { paketlemeTipleri, paketlemeTipiOptions } = useLookups();
  const paketlemeAdById = Object.fromEntries(paketlemeTipleri.map(p => [p.id, p.ad]));

  const [musteriler, setMusteriler] = useState([]);
  const [gununMenusu, setGununMenusu] = useState(null);
  const [urunler, setUrunler] = useState([]);
  const [listeFiyatlari, setListeFiyatlari] = useState([]);
  const [kisiDegisiklikler, setKisiDegisiklikler] = useState({});
  const [paketlemeDegisiklikler, setPaketlemeDegisiklikler] = useState({});
  const [loading, setLoading] = useState(false);
  const [onaylananlar, setOnaylananlar] = useState({});
  const [onaylananKisi, setOnaylananKisi] = useState({}); // musteri.id -> mevcut satıştaki kişi adeti
  const [planKisi, setPlanKisi] = useState({}); // cari_id -> {kisi, dahil} (üretim planından)
  const [seciliIds, setSeciliIds] = useState([]);
  const [islemYapiliyor, setIslemYapiliyor] = useState(false);
  const [tarih, setTarih] = useState(dayjs());

  const gunIndex = tarih.day();
  const gunKey = gunKeyMap[gunIndex];
  const gunAdi = gunAdlari[gunIndex];

  const fetchData = async () => {
    setLoading(true);
    try {
      const tarihStr = tarih.format('YYYY-MM-DD');
      const [musteriRes, menuRes, satisRes, planRes, urunRes, listeRes] = await Promise.all([
        musterilerAPI.getAll(),
        gununMenuleriAPI.getByDate(tarihStr),
        satislarAPI.getAll(),
        gunUretimPlaniAPI.getByDate(tarihStr).catch(() => ({ data: null })),
        urunlerAPI.getAll(),
        listeFiyatlariAPI.getAll(),
      ]);
      const aktifList = musteriRes.data.filter(m => m.aktif && m.cari_aktif);
      setMusteriler(aktifList);
      setGununMenusu(menuRes.data || null);
      setUrunler(urunRes.data || []);
      setListeFiyatlari(listeRes.data || []);

      // Üretim planındaki müşteri kişi sayıları (cari_id → {kisi, dahil})
      const yeniPlan = {};
      (planRes?.data?.musteriler || []).forEach(pm => {
        yeniPlan[pm.cari_id] = { kisi: Number(pm.kisi || 0), dahil: !!pm.dahil };
      });
      setPlanKisi(yeniPlan);

      // Seçilen tarihteki sabit (gelir_tipi=2) satışları işaretle — sayfa yenilense bile
      // aynı müşteri için tekrar onaylama yapılmasın diye.
      const cariIdToMusteriId = {};
      aktifList.forEach(m => { if (m.cari_id) cariIdToMusteriId[m.cari_id] = m.id; });

      const sabitSatislar = (satisRes.data || []).filter(s =>
        s.gelir_tipi_id === SABIT_GELIR_TIPI_ID &&
        dayjs(s.tarih).format('YYYY-MM-DD') === tarihStr
      );

      const yeniOnaylananlar = {};
      sabitSatislar.forEach(s => {
        const musteriId = cariIdToMusteriId[s.cari_id];
        if (musteriId && !yeniOnaylananlar[musteriId]) {
          yeniOnaylananlar[musteriId] = s.id;
        }
      });

      // Onaylanmış satışların kalemlerini çek → kişi sayısı = menü kalemi adet'i
      const detaylar = await Promise.all(
        sabitSatislar.map(s => satislarAPI.getById(s.id).catch(() => null))
      );
      const yeniOnaylananKisi = {};
      detaylar.forEach((det) => {
        if (!det?.data) return;
        const satis = det.data;
        const musteriId = cariIdToMusteriId[satis.cari_id];
        const menuKalem = (satis.kalemler || []).find(k => k.tur === 'menu');
        if (musteriId && menuKalem) {
          yeniOnaylananKisi[musteriId] = Number(menuKalem.adet || 0);
        }
      });
      setOnaylananKisi(yeniOnaylananKisi);

      setKisiDegisiklikler({});
      setPaketlemeDegisiklikler({});
      setOnaylananlar(yeniOnaylananlar);
      setSeciliIds([]);
    } catch (err) {
      message.error('Veriler yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [tarih]);

  const getKisiSayisi = (musteri) => {
    // 1) Onaylanmış satış varsa: satıştaki gerçek adet
    if (onaylananlar[musteri.id] && onaylananKisi[musteri.id] !== undefined) {
      return onaylananKisi[musteri.id];
    }
    // 2) Kullanıcı bu oturumda değiştirmişse
    if (kisiDegisiklikler[musteri.id] !== undefined) return kisiDegisiklikler[musteri.id];
    // 3) Üretim planındaki değer (dahil değilse 0)
    const plan = planKisi[musteri.cari_id];
    if (plan) return plan.dahil ? plan.kisi : 0;
    // 4) Fallback: müşteri tablosundaki haftalık varsayılan
    return Number(musteri[gunKey] || 0);
  };

  const handleKisiDegistir = (musteriId, value) => {
    setKisiDegisiklikler(prev => ({ ...prev, [musteriId]: value }));
  };

  const getPaketleme = (musteri) => {
    if (paketlemeDegisiklikler[musteri.id] !== undefined) return paketlemeDegisiklikler[musteri.id];
    return musteri.paketleme_tipi_id || 2;
  };

  const handlePaketlemeDegistir = (musteriId, value) => {
    setPaketlemeDegisiklikler(prev => ({ ...prev, [musteriId]: value }));
  };

  const aktifMusteriler = useMemo(() => {
    return musteriler
      .sort((a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr'));
  }, [musteriler]);

  const onaylanmamislar = useMemo(() => {
    return aktifMusteriler.filter(m => !onaylananlar[m.id] && getKisiSayisi(m) > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktifMusteriler, onaylananlar, kisiDegisiklikler, planKisi]);

  // Ürün liste fiyatı (KDV dahil): liste_fiyatlari (tur='urun') → yoksa ürün satis_fiyati.
  const getUrunListeFiyati = (itemId) => {
    const urun = urunler.find(u => u.id === itemId);
    if (urun && urun.urun_tipi_id) {
      const lf = listeFiyatlari.find(l => l.tur === 'urun' && l.urun_tipi_id === urun.urun_tipi_id && l.aktif);
      if (lf) return Number(lf.fiyat);
    }
    if (urun && urun.satis_fiyati) return Number(urun.satis_fiyati);
    return 0;
  };

  // Günün menüsü set fiyatı (KDV dahil): ürün liste fiyatları toplamı − global menü indirimi.
  const getMenuSetFiyatiDahil = () => {
    if (!gununMenusu || !gununMenusu.kalemler?.length) return 0;
    const urunToplam = gununMenusu.kalemler.reduce((t, k) => t + getUrunListeFiyati(k.urun_id) * (k.adet || 1), 0);
    const indirimRec = listeFiyatlari.find(l => l.tur === 'indirim' && l.aktif);
    const indirim = indirimRec ? Number(indirimRec.fiyat) : 0;
    return Math.max(0, urunToplam - indirim);
  };

  // Müşterinin fiyat tipine göre KDV HARİÇ efektif birim fiyatı (kalem birim_fiyat'ı).
  //  'sabit'         → anlasma_fiyati (KDV hariç sabit birim fiyat)
  //  'tutar_indirim' → (günün menüsü KDV dahil − indirim TL) → KDV hariç'e çevir
  //  'yuzde_indirim' → (günün menüsü KDV dahil × (1−%))      → KDV hariç'e çevir
  const getEffektifBirimFiyat = (m) => {
    const tipi = m.anlasma_fiyat_tipi || 'sabit';
    const deger = Number(m.anlasma_fiyati) || 0;
    if (tipi === 'sabit') return deger;
    const tabanDahil = getMenuSetFiyatiDahil();
    let indirimliDahil = tabanDahil;
    if (tipi === 'tutar_indirim') indirimliDahil = Math.max(0, tabanDahil - deger);
    else if (tipi === 'yuzde_indirim') indirimliDahil = Math.max(0, tabanDahil * (1 - deger / 100));
    const kdvCarpan = m.faturali ? 1.10 : 1;
    return Number((indirimliDahil / kdvCarpan).toFixed(2));
  };

  const toplamlar = useMemo(() => {
    let toplamKisi = 0;
    let toplamTutar = 0;
    let onaylananToplamKisi = 0;
    let onaylananTutar = 0;
    aktifMusteriler.forEach(m => {
      const kisi = getKisiSayisi(m);
      const fiyat = getEffektifBirimFiyat(m);
      const kdvCarpan = m.faturali ? 1.10 : 1;
      const tutar = kisi * fiyat * kdvCarpan;
      toplamKisi += kisi;
      toplamTutar += tutar;
      if (onaylananlar[m.id]) {
        onaylananToplamKisi += kisi;
        onaylananTutar += tutar;
      }
    });
    return {
      musteri: aktifMusteriler.length,
      kisi: toplamKisi,
      tutar: toplamTutar,
      onaylanan: Object.keys(onaylananlar).length,
      onaylananKisi: onaylananToplamKisi,
      onaylananTutar,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aktifMusteriler, kisiDegisiklikler, onaylananlar, planKisi, onaylananKisi, gununMenusu, urunler, listeFiyatlari]);

  const satisOlustur = async (musteri) => {
    const kisi = getKisiSayisi(musteri);
    const anlasmFiyat = getEffektifBirimFiyat(musteri);
    const tipi = musteri.anlasma_fiyat_tipi || 'sabit';
    if (tipi !== 'sabit' && anlasmFiyat <= 0) {
      throw new Error('İndirimli fiyat hesaplanamadı (günün menüsü tanımlı değil veya indirim menü fiyatını aşıyor)');
    }
    const kdvOrani = musteri.faturali ? 0.10 : 0;

    const payload = {
      cari_id: musteri.cari_id,
      gelir_tipi_id: SABIT_GELIR_TIPI_ID,
      fatura_durumu_id: musteri.faturali ? 3 : 4,
      tarih: tarih.format('YYYY-MM-DD'),
      aciklama: `${kisi} kişi - ${gunAdi} paketleme - ${paketlemeAdById[getPaketleme(musteri)] || ''}`,
      kalemler: [{
        tur: 'menu',
        urun_id: null,
        adet: kisi,
        birim_fiyat: anlasmFiyat,
        kdv_tutar: Number((anlasmFiyat * kisi * kdvOrani).toFixed(2)),
        kdv_dahil_tutar: Number((anlasmFiyat * kisi * (1 + kdvOrani)).toFixed(2)),
        maliyet: 0,
      }],
      hemen_odendi: false,
      kasa_id: null,
    };

    const res = await satislarAPI.create(payload);
    return { satisId: res.data?.id, kisi };
  };

  // Tekli onaylama
  const handleTekOnayla = async (musteri) => {
    if (!gununMenusu) { message.error('Seçilen tarih için menü tanımlanmamış'); return; }
    if (getKisiSayisi(musteri) <= 0) { message.error('Kişi sayısı 0 olan müşteri onaylanamaz'); return; }

    setIslemYapiliyor(true);
    try {
      const { satisId, kisi } = await satisOlustur(musteri);
      setOnaylananlar(prev => ({ ...prev, [musteri.id]: satisId }));
      setOnaylananKisi(prev => ({ ...prev, [musteri.id]: kisi }));
      message.success(`${musteri.ad} - satışa eklendi`);
    } catch (err) {
      message.error(`${musteri.ad} - hata: ${err.response?.data?.hata || err.message}`);
    } finally {
      setIslemYapiliyor(false);
    }
  };

  // Onayı geri al — satışı sil
  const handleGeriAl = async (musteri) => {
    const satisId = onaylananlar[musteri.id];
    if (!satisId) { message.error('Bu müşteri için satış bulunamadı'); return; }
    setIslemYapiliyor(true);
    try {
      await satislarAPI.remove(satisId);
      setOnaylananlar(prev => {
        const yeni = { ...prev };
        delete yeni[musteri.id];
        return yeni;
      });
      setOnaylananKisi(prev => {
        const yeni = { ...prev };
        delete yeni[musteri.id];
        return yeni;
      });
      message.success(`${musteri.ad} - satıştan kaldırıldı`);
    } catch (err) {
      message.error(`${musteri.ad} - geri alınamadı: ${err.response?.data?.hata || err.message}`);
    } finally {
      setIslemYapiliyor(false);
    }
  };

  // Seçilenleri toplu onayla
  const handleSecilenleriOnayla = async () => {
    if (!gununMenusu) { message.error('Seçilen tarih için menü tanımlanmamış'); return; }
    const onaylanacaklar = aktifMusteriler.filter(m => seciliIds.includes(m.id) && !onaylananlar[m.id] && getKisiSayisi(m) > 0);
    if (onaylanacaklar.length === 0) { message.warning('Onaylanacak seçili müşteri yok'); return; }

    setIslemYapiliyor(true);
    let basarili = 0;
    let hatali = 0;

    for (const musteri of onaylanacaklar) {
      try {
        const { satisId, kisi } = await satisOlustur(musteri);
        setOnaylananlar(prev => ({ ...prev, [musteri.id]: satisId }));
        setOnaylananKisi(prev => ({ ...prev, [musteri.id]: kisi }));
        basarili++;
      } catch (err) {
        hatali++;
        console.error(`Satış oluşturulamadı (${musteri.ad}):`, err);
      }
    }

    setIslemYapiliyor(false);
    setSeciliIds(prev => prev.filter(id => !onaylanacaklar.find(m => m.id === id)));

    if (hatali === 0) {
      message.success(`${basarili} müşteri için satış oluşturuldu`);
    } else {
      message.warning(`${basarili} başarılı, ${hatali} hatalı`);
    }
  };

  // Tümünü onayla
  const handleTumunuOnayla = async () => {
    if (!gununMenusu) { message.error('Seçilen tarih için menü tanımlanmamış'); return; }
    if (onaylanmamislar.length === 0) { message.warning('Onaylanacak müşteri yok'); return; }

    setIslemYapiliyor(true);
    let basarili = 0;
    let hatali = 0;

    for (const musteri of onaylanmamislar) {
      try {
        const { satisId, kisi } = await satisOlustur(musteri);
        setOnaylananlar(prev => ({ ...prev, [musteri.id]: satisId }));
        setOnaylananKisi(prev => ({ ...prev, [musteri.id]: kisi }));
        basarili++;
      } catch (err) {
        hatali++;
        console.error(`Satış oluşturulamadı (${musteri.ad}):`, err);
      }
    }

    setIslemYapiliyor(false);

    if (hatali === 0) {
      message.success(`${basarili} müşteri için satış oluşturuldu`);
    } else {
      message.warning(`${basarili} başarılı, ${hatali} hatalı`);
    }
  };

  // Checkbox seçim
  const handleSecimDegistir = (musteriId, checked) => {
    setSeciliIds(prev => checked ? [...prev, musteriId] : prev.filter(id => id !== musteriId));
  };

  const handleTumunuSec = (checked) => {
    if (checked) {
      setSeciliIds(onaylanmamislar.map(m => m.id));
    } else {
      setSeciliIds([]);
    }
  };

  const tumunuSeciliMi = onaylanmamislar.length > 0 && onaylanmamislar.every(m => seciliIds.includes(m.id));
  const bazilariSeciliMi = seciliIds.length > 0 && !tumunuSeciliMi;

  const columns = [
    {
      title: (
        <Checkbox
          checked={tumunuSeciliMi}
          indeterminate={bazilariSeciliMi}
          onChange={(e) => handleTumunuSec(e.target.checked)}
        />
      ),
      key: 'secim',
      width: 50,
      align: 'center',
      render: (_, record) => {
        if (onaylananlar[record.id]) return <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />;
        return (
          <Checkbox
            checked={seciliIds.includes(record.id)}
            onChange={(e) => handleSecimDegistir(record.id, e.target.checked)}
          />
        );
      },
    },
    {
      title: 'Müşteri', dataIndex: 'ad', key: 'ad',
      render: (v, record) => (
        <span style={{ opacity: onaylananlar[record.id] ? 0.5 : 1 }}>{v}</span>
      ),
      sorter: (a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr'),
    },
    {
      title: 'Paketleme', key: 'paketleme', width: 140, align: 'center', responsive: ['md'],
      render: (_, record) => {
        const val = getPaketleme(record);
        if (onaylananlar[record.id]) {
          return <Tag color={PAKETLEME_RENK[val] || 'default'}>{paketlemeAdById[val] || '-'}</Tag>;
        }
        return (
          <Select
            size="small"
            value={val}
            onChange={(v) => handlePaketlemeDegistir(record.id, v)}
            style={{ width: 120 }}
            options={paketlemeTipiOptions}
          />
        );
      },
    },
    {
      title: <span style={{ display: 'inline-block', whiteSpace: 'pre-line', lineHeight: 1.2 }}>{'Kişi\nSayısı'}</span>,
      key: 'kisi', width: isMobile ? 70 : 120, align: 'center',
      render: (_, record) => {
        if (onaylananlar[record.id]) return <span style={{ color: '#52c41a', fontWeight: 600 }}>{getKisiSayisi(record)}</span>;
        return (
          <InputNumber
            min={0}
            value={getKisiSayisi(record)}
            onChange={(v) => handleKisiDegistir(record.id, v || 0)}
            size="small"
            style={{ width: isMobile ? 56 : 80 }}
          />
        );
      },
    },
    {
      title: 'Anlaşma Fiyatı', dataIndex: 'anlasma_fiyati', key: 'anlasma_fiyati', width: 140, align: 'right', responsive: ['md'],
      render: (v, r) => {
        const tipi = r.anlasma_fiyat_tipi || 'sabit';
        if (tipi === 'sabit') return v != null ? formatPara(v) : '-';
        const eff = getEffektifBirimFiyat(r);
        const etiket = tipi === 'yuzde_indirim' ? `%${Number(v)} ind.` : `${formatPara(v)} ind.`;
        return (
          <span>
            <strong>{eff > 0 ? formatPara(eff) : '—'}</strong>
            <div style={{ fontSize: 11, color: eff > 0 ? '#888' : '#cf1322' }}>
              {etiket}{!gununMenusu ? ' · menü yok' : (eff <= 0 ? ' · fiyat 0' : '')}
            </div>
          </span>
        );
      },
    },
    {
      title: 'Toplam', key: 'toplam', width: 140, align: 'right',
      render: (_, record) => {
        const kisi = getKisiSayisi(record);
        const fiyat = getEffektifBirimFiyat(record);
        const kdvCarpan = record.faturali ? 1.10 : 1;
        const toplam = kisi * fiyat * kdvCarpan;
        if (toplam <= 0) return '-';
        return (
          <span>
            <strong>{formatPara(toplam)}</strong>
            <div style={{ fontSize: 11, color: '#888' }}>
              {record.faturali ? 'KDV Dahil' : 'KDV Yok'}
            </div>
          </span>
        );
      },
    },
    {
      title: 'Durum', key: 'durum', width: isMobile ? 120 : 180, align: 'center',
      render: (_, record) => {
        if (onaylananlar[record.id]) {
          return (
            <Space size={4}>
              <Tag color="green" icon={<CheckCircleOutlined />}>Satışa Eklendi</Tag>
              <Popconfirm
                title="Onayı geri alacaksınız. Satıştan kaldırılacak. Devam?"
                onConfirm={() => handleGeriAl(record)}
                okText="Evet"
                cancelText="Hayır"
              >
                <Button size="small" danger icon={<UndoOutlined />} disabled={islemYapiliyor} title="Geri Al" />
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => handleTekOnayla(record)}
            disabled={islemYapiliyor || getKisiSayisi(record) <= 0 || !gununMenusu}
          >
            Onayla
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Paketleme</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading} title="Yenile">
            {!isMobile && 'Yenile'}
          </Button>
        </Space>
      </div>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 12]} align="middle">
          <Col xs={24} sm={8} md={5}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Tarih</div>
            <DatePicker
              value={tarih}
              onChange={(d) => setTarih(d || dayjs())}
              allowClear={false}
              format="DD.MM.YYYY"
              style={{ width: '100%' }}
            />
            <div style={{ fontSize: 14, color: '#FF6B00', fontWeight: 600, marginTop: 4 }}>{gunAdi}</div>
          </Col>
          <Col xs={24} sm={8} md={5}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Günün Menüsü</div>
            {gununMenusu ? (
              <div style={{ fontSize: 15, fontWeight: 600, color: '#FF6B00' }}>{gununMenusu.aciklama || `${gununMenusu.kalemler.length} kalem`}</div>
            ) : (
              <Alert message="Seçilen tarih için menü tanımlı değil. Satış takviminden tanımlayın." type="warning" showIcon style={{ padding: '4px 8px' }} />
            )}
          </Col>
          <Col xs={8} sm={4} md={3}>
            <Statistic title="Müşteri" value={toplamlar.musteri} />
          </Col>
          <Col xs={8} sm={4} md={3}>
            <Statistic title="Toplam Kişi" value={toplamlar.kisi} />
          </Col>
          <Col xs={8} sm={4} md={3}>
            <Statistic title="Onaylanan" value={toplamlar.onaylanan} valueStyle={{ color: '#3f8600' }} suffix={`/ ${toplamlar.musteri}`} />
          </Col>
          <Col xs={24} sm={8} md={5}>
            <Statistic title="Toplam Tutar" value={toplamlar.tutar} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} />
          </Col>
        </Row>
      </Card>

      {gununMenusu && gununMenusu.kalemler && gununMenusu.kalemler.length > 0 && (
        <Card size="small" style={{ marginBottom: 16, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
          <strong>Günün Menüsü: </strong>
          {gununMenusu.aciklama ? <span style={{ marginRight: 12 }}>{gununMenusu.aciklama}</span> : null}
          <span style={{ color: '#666' }}>
            ({gununMenusu.kalemler.map(k => `${k.urun_adi}${k.adet > 1 ? ` ×${k.adet}` : ''}`).join(', ')})
          </span>
        </Card>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Space>
          {seciliIds.length > 0 && (
            <Popconfirm
              title={`${seciliIds.filter(id => !onaylananlar[id]).length} müşteri için satış oluşturulacak. Devam?`}
              onConfirm={handleSecilenleriOnayla}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button type="primary" icon={<CheckOutlined />} loading={islemYapiliyor}>
                Seçilenleri Onayla ({seciliIds.filter(id => !onaylananlar[id]).length})
              </Button>
            </Popconfirm>
          )}
          {onaylanmamislar.length > 0 && (
            <Popconfirm
              title={`Tüm ${onaylanmamislar.length} müşteri için satış oluşturulacak. Devam?`}
              onConfirm={handleTumunuOnayla}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button icon={<CheckCircleOutlined />} loading={islemYapiliyor}>
                Tümünü Onayla ({onaylanmamislar.length})
              </Button>
            </Popconfirm>
          )}
        </Space>
        {toplamlar.onaylanan > 0 && (
          <Tag color="green" style={{ fontSize: 13, padding: '4px 12px' }}>
            {toplamlar.onaylanan} onaylandı - {formatPara(toplamlar.onaylananTutar)}
          </Tag>
        )}
      </div>

      <Table
        columns={columns}
        dataSource={aktifMusteriler}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
        rowClassName={(record) => onaylananlar[record.id] ? 'ant-table-row-selected' : ''}
      />
    </div>
  );
}

export default Paketleme;
